import { cloneElement, isValidElement, useId, useLayoutEffect, useRef, useState,
  type HTMLAttributes, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { focusableElements } from './overlay-utils';

export interface PopoverProps {
  trigger: ReactElement; children: ReactNode; label: string; open?: boolean; onOpenChange?: (open: boolean) => void;
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'; className?: string;
  triggerMode?: 'click' | 'long-press'; role?: 'dialog' | 'menu';
}

export function Popover({ trigger, children, label, open: controlled, onOpenChange, placement = 'bottom-end', className,
  triggerMode = 'click', role = 'dialog' }: PopoverProps) {
  const [localOpen, setLocalOpen] = useState(false); const open = controlled ?? localOpen;
  const anchor = useRef<HTMLSpanElement>(null); const content = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const origin = useRef({ x: 0, y: 0 }); const pressed = useRef(false); const id = useId();
  const change = (next: boolean) => { if (controlled === undefined) setLocalOpen(next); onOpenChange?.(next); };
  const changeRef = useRef(change); useLayoutEffect(() => { changeRef.current = change; });
  useLayoutEffect(() => () => clearTimeout(longPressTimer.current), []);
  useLayoutEffect(() => {
    if (!open || !anchor.current || !content.current) return;
    const panel = content.current; const triggerElement = anchor.current.querySelector<HTMLElement>('button, a, [tabindex]');
    const update = () => {
      if (!anchor.current) return;
      const bounds = anchor.current.getBoundingClientRect(); const rect = panel.getBoundingClientRect(); const margin = 8;
      const rtl = getComputedStyle(anchor.current).direction === 'rtl';
      const end = placement.endsWith('end') !== rtl;
      const left = Math.max(margin, Math.min(window.innerWidth - rect.width - margin, end ? bounds.right - rect.width : bounds.left));
      const top = placement.startsWith('top') || bounds.bottom + rect.height + margin > window.innerHeight
        ? Math.max(margin, bounds.top - rect.height - margin) : bounds.bottom + margin;
      const owner = anchor.current.closest('.hylo-sheet, .hylo-alert')?.getBoundingClientRect();
      Object.assign(panel.style, { position: 'fixed', left: `${left - (owner?.left ?? 0)}px`, top: `${top - (owner?.top ?? 0)}px`,
        visibility: 'visible', maxHeight: `${window.innerHeight - margin * 2}px` });
    };
    update(); (focusableElements(panel)[0] ?? panel).focus();
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !panel.contains(event.target) && !anchor.current?.contains(event.target)) changeRef.current(false);
    };
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) { event.preventDefault(); changeRef.current(false); }
      if (event.key === 'Tab' && role === 'menu') changeRef.current(false);
    };
    document.addEventListener('pointerdown', outside); document.addEventListener('keydown', keydown);
    window.addEventListener('resize', update); document.addEventListener('scroll', update, true);
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(update); observer?.observe(panel);
    return () => {
      document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', keydown);
      window.removeEventListener('resize', update); document.removeEventListener('scroll', update, true); observer?.disconnect();
      if (triggerElement?.isConnected) triggerElement.focus();
    };
  }, [open, placement, role]);
  const triggerProps = isValidElement<HTMLAttributes<HTMLElement>>(trigger) ? trigger.props : {};
  return <><span ref={anchor} className="hylo-popover-anchor"
    onClickCapture={event => { if (pressed.current) { event.preventDefault(); event.stopPropagation(); pressed.current = false; } }}
    onContextMenu={event => { if (triggerMode === 'long-press') { event.preventDefault(); change(true); } }}
    onPointerDown={event => {
      if (triggerMode !== 'long-press' || event.pointerType === 'mouse') return;
      origin.current = { x: event.clientX, y: event.clientY }; pressed.current = false;
      longPressTimer.current = setTimeout(() => { pressed.current = true; change(true); }, 500);
    }} onPointerMove={event => {
      if (Math.abs(event.clientX - origin.current.x) + Math.abs(event.clientY - origin.current.y) > 10) clearTimeout(longPressTimer.current);
    }} onPointerUp={() => clearTimeout(longPressTimer.current)} onPointerCancel={() => clearTimeout(longPressTimer.current)}>
    {cloneElement(trigger as ReactElement<HTMLAttributes<HTMLElement>>, {
      'aria-haspopup': role, 'aria-expanded': open, 'aria-controls': open ? id : undefined,
      onClick: event => {
        if (pressed.current) { event.preventDefault(); pressed.current = false; return; }
        triggerProps.onClick?.(event); if (triggerMode === 'click' && !event.defaultPrevented) change(!open);
      },
      onKeyDown: event => {
        triggerProps.onKeyDown?.(event);
        if (event.defaultPrevented) return;
        if (event.key === 'ArrowDown' || (event.shiftKey && event.key === 'F10')) { event.preventDefault(); change(true); }
      },
    })}
  </span>{open && createPortal(<div id={id} ref={content} role={role} tabIndex={-1} aria-label={label}
    className={clsx('hylo-popover', 'hylo-glass', className)} style={{ visibility: 'hidden' }}
    onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); change(false); } }}
    >{children}</div>, anchor.current?.closest('.hylo-sheet, .hylo-alert') ?? document.body)}</>;
}
